import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogIn, KeyRound, Mail, GraduationCap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/university`;

export default function UniversityStudentLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | reset
  const [form, setForm] = useState({ email: "", password: "", setup_token: "", name: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [resetIssued, setResetIssued] = useState(null);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await axios.post(`${API}/students/login`, { email: form.email.trim(), password: form.password });
        localStorage.setItem("kcis_student_token", r.data.token);
        localStorage.setItem("kcis_student_email", r.data.student.email);
        toast.success("Welcome back");
        navigate(`/university/student`);
      } else if (mode === "register") {
        const r = await axios.post(`${API}/students/register`, {
          email: form.email.trim(), password: form.password, setup_token: form.setup_token.trim(), name: form.name,
        });
        localStorage.setItem("kcis_student_token", r.data.token);
        localStorage.setItem("kcis_student_email", r.data.student.email);
        toast.success("Account created — welcome to KISSED University");
        navigate(`/university/student`);
      } else if (mode === "reset_request") {
        const r = await axios.post(`${API}/students/password-reset`, { email: form.email.trim() });
        setResetIssued({ token: r.data.preview_reset_token });
        toast.success("If that email is on file, a reset link was sent.");
      } else if (mode === "reset_set") {
        await axios.post(`${API}/students/password-set`, { reset_token: form.setup_token.trim(), new_password: form.new_password });
        toast.success("Password updated — please log in");
        setMode("login");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/university" className="inline-flex items-center gap-2 text-purple-900">
            <GraduationCap className="w-5 h-5" />
            <span className="font-display text-lg">KISSED University</span>
          </Link>
          <h1 className="mt-3 font-display text-3xl text-slate-900">
            {mode === "login" && "Student Login"}
            {mode === "register" && "Create your Student Account"}
            {mode === "reset_request" && "Reset your Password"}
            {mode === "reset_set" && "Set a New Password"}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {mode === "register" && "Use the setup token sent to you after enrollment / application payment."}
            {mode === "login" && "Sign in to access your courses, certificates, and alumni profile."}
            {mode === "reset_request" && "Enter your email to receive a reset token."}
            {mode === "reset_set" && "Paste the token from your reset email."}
          </p>
        </div>

        <form onSubmit={onSubmit} className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3" data-testid="uni-student-login-form">
          {(mode === "login" || mode === "register" || mode === "reset_request") && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Email</label>
              <div className="mt-1 relative">
                <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full rounded-xl border border-slate-200 pl-9 pr-3 h-10 text-sm" data-testid="uni-student-email" />
              </div>
            </div>
          )}
          {mode === "register" && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Name</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 h-10 text-sm" />
            </div>
          )}
          {mode === "register" && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Setup token</label>
              <input required value={form.setup_token} onChange={(e) => set("setup_token", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 h-10 text-sm font-mono" placeholder="Provided in your enrollment confirmation" data-testid="uni-student-setup-token" />
            </div>
          )}
          {mode === "reset_set" && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Reset token</label>
              <input required value={form.setup_token} onChange={(e) => set("setup_token", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 h-10 text-sm font-mono" />
            </div>
          )}
          {(mode === "login" || mode === "register") && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">Password</label>
              <div className="mt-1 relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <input type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="w-full rounded-xl border border-slate-200 pl-9 pr-3 h-10 text-sm" data-testid="uni-student-password" />
              </div>
            </div>
          )}
          {mode === "reset_set" && (
            <div>
              <label className="text-[11px] uppercase tracking-wide text-slate-500">New password</label>
              <input type="password" required minLength={8} value={form.new_password} onChange={(e) => set("new_password", e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 px-3 h-10 text-sm" />
            </div>
          )}

          <button type="submit" disabled={busy} className="w-full rounded-xl bg-purple-900 hover:bg-purple-800 text-white font-medium h-10 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="uni-student-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
            {mode === "login" && "Sign in"}
            {mode === "register" && "Create account"}
            {mode === "reset_request" && "Send reset link"}
            {mode === "reset_set" && "Set new password"}
          </button>

          {resetIssued?.token && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="uni-student-reset-preview">
              <div className="font-medium">Preview environment only:</div>
              <div className="mt-1 font-mono break-all">{resetIssued.token}</div>
              <button type="button" className="mt-2 text-emerald-900 underline" onClick={() => { set("setup_token", resetIssued.token); setMode("reset_set"); setResetIssued(null); }}>Use this token →</button>
            </div>
          )}
        </form>

        <div className="mt-4 text-center text-xs text-slate-600 space-x-3">
          {mode !== "login" && <button onClick={() => setMode("login")} className="text-purple-900 hover:underline" data-testid="uni-student-link-login">Have an account? Sign in</button>}
          {mode !== "register" && <button onClick={() => setMode("register")} className="text-purple-900 hover:underline" data-testid="uni-student-link-register">First time? Create account</button>}
          {mode !== "reset_request" && mode !== "reset_set" && <button onClick={() => setMode("reset_request")} className="text-purple-900 hover:underline">Forgot password?</button>}
        </div>
      </div>
    </div>
  );
}
